import React, { useState, useEffect, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

const LiftingAdvicePrint = forwardRef(({ adviceId, adviceData, companyData, docketData }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [advice, setAdvice] = useState(adviceData || null);
  const [docket, setDocket] = useState(docketData || null);
  const [custodianAddress, setCustodianAddress] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [loading, setLoading] = useState(!adviceData || !companyData);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (adviceData && companyData) {
        setAdvice(adviceData);
        setCompany(companyData);
        if (docketData?.payload_json) {
          setDocket(docketData);
        } else {
          const docketIdToFetch = docketData?.id || adviceData?.docket_id;
          if (docketIdToFetch) {
            fetch(`${backendUrl}/api/dockets/${docketIdToFetch}`, { headers: getAuthHeaders() })
              .then(res => res.ok ? res.json() : null)
              .then(d => { if (d) setDocket(d); })
              .catch(console.warn);
          }
        }
        setLoading(false);
        return;
      }

      try {
        // Fetch advice data
        const adviceResponse = await fetch(`${backendUrl}/api/lifting-advices/${adviceId}`, {
          headers: getAuthHeaders()
        });
        if (adviceResponse.ok) {
          const aData = await adviceResponse.json();
          setAdvice(aData);

          // Fetch docket data first so we can use its company_id
          let docketCompanyId = null;
          if (aData.docket_id) {
            const docketResponse = await fetch(`${backendUrl}/api/dockets/${aData.docket_id}`, {
              headers: getAuthHeaders()
            });
            if (docketResponse.ok) {
              const dData = await docketResponse.json();
              setDocket(dData);
              docketCompanyId = dData.company_id || null;
            }
          }

          // Fetch company using the docket's company_id (or advice's), falling
          // back to the logged-in user's company only as a last resort.
          const companyId = docketCompanyId || aData.company_id || localStorage.getItem('company_id');
          if (companyId) {
            const companyResponse = await fetch(`${backendUrl}/api/companies/${companyId}`, {
              headers: getAuthHeaders()
            });
            if (companyResponse.ok) {
              setCompany(await companyResponse.json());
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adviceId, adviceData, companyData, backendUrl]);

  // Resolve custodian address: prefer the saved value on the advice,
  // otherwise look it up by id from the master (so legacy records still
  // render correctly).
  useEffect(() => {
    if (advice?.employee_name) {
      setEmployeeName(advice.employee_name);
      return;
    }
    if (advice?.employee_id) {
      fetch(`${backendUrl}/api/users/${advice.employee_id}`, { headers: getAuthHeaders() })
        .then(res => res.ok ? res.json() : null)
        .then(u => {
          if (u) setEmployeeName(u.full_name || u.name || u.email || '');
        })
        .catch(() => {});
    } else {
      setEmployeeName('');
    }
  }, [advice?.employee_id, advice?.employee_name, backendUrl]);

  useEffect(() => {
    const resolveCustodian = async () => {
      if (!advice) return setCustodianAddress('');
      if (advice.to_custodian_address) {
        setCustodianAddress(advice.to_custodian_address);
        return;
      }
      if (advice.to_custodian_id) {
        try {
          const res = await fetch(`${backendUrl}/api/custodians/${advice.to_custodian_id}`, {
            headers: getAuthHeaders(),
          });
          if (res.ok) {
            const c = await res.json();
            setCustodianAddress(c.address || '');
            return;
          }
        } catch (_) {}
      }
      // Final fallback: match against the custodian master for legacy
      // advices that have only `to_location_text` and no linked custodian id.
      // We prefer an exact name match, then a "starts-with" match, and
      // finally any substring hit returned by the API's ILIKE search.
      if (advice.to_location_text) {
        try {
          const res = await fetch(`${backendUrl}/api/custodians?search=${encodeURIComponent(advice.to_location_text)}`, {
            headers: getAuthHeaders(),
          });
          if (res.ok) {
            const list = await res.json();
            const needle = advice.to_location_text.trim().toLowerCase();
            const exact = list.find(c => (c.name || '').trim().toLowerCase() === needle);
            const startsWith = list.find(c => (c.name || '').trim().toLowerCase().startsWith(needle));
            const pick = exact || startsWith || list[0];
            if (pick && pick.address) {
              setCustodianAddress(pick.address);
              return;
            }
          }
        } catch (_) {}
      }
      setCustodianAddress('');
    };
    resolveCustodian();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advice?.id, advice?.to_custodian_address, advice?.to_custodian_id, advice?.to_location_text]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Compute multi-row HAWB data from Master Consolidation house dockets or comma-separated HAWB string
  const hawbRows = React.useMemo(() => {
    const consolSummary = docket?.payload_json?.consolidation?.house_dockets_summary || [];
    
    // 1. If docket has attached house dockets from Master Consolidation:
    if (consolSummary.length > 0) {
      return consolSummary.map((hd, idx) => ({
        sn: idx + 1,
        hawb_no: (hd.hawb_no || hd.system_docket_no || '').trim(),
        contents: advice?.contents || hd.cargo_type || '',
        pieces: hd.packages !== undefined && hd.packages !== null ? hd.packages : '',
        weight: hd.gross_weight !== undefined && hd.gross_weight !== null ? Number(hd.gross_weight).toFixed(2) : '',
        from: hd.origin || advice?.from_location_name || '',
        to: hd.destination || advice?.to_location_final_name || ''
      }));
    }

    // 2. Otherwise, check if advice.hawb_no contains comma-separated values:
    const rawHawb = advice?.hawb_no || '';
    const hawbList = rawHawb.split(',').map(s => s.trim()).filter(Boolean);
    
    if (hawbList.length > 1) {
      return hawbList.map((h, idx) => ({
        sn: idx + 1,
        hawb_no: h,
        contents: advice?.contents || '',
        pieces: '',
        weight: '',
        from: advice?.from_location_name || '',
        to: advice?.to_location_final_name || ''
      }));
    }

    // 3. Default single row
    return [{
      sn: 1,
      hawb_no: rawHawb,
      contents: advice?.contents || '',
      pieces: advice?.no_of_pieces || '',
      weight: advice?.gross_weight ? Number(advice.gross_weight).toFixed(2) : '',
      from: advice?.from_location_name || '',
      to: advice?.to_location_final_name || ''
    }];
  }, [docket, advice]);


  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const companyName = company?.name || 'COMPANY NAME';
  const companyLogo = company?.logo_url;
  const companyAddress = company?.address || '';
  const companyCity = company?.city || '';
  const companyState = company?.state || '';
  const companyPincode = company?.pincode || '';
  const companyPhone = company?.phone || '';
  const companyEmail = company?.email || '';
  const companyWebsite = company?.website || '';
  const companyGst = company?.gst_number || '';
  const companyLlp = company?.cin_number || '';

  const fullAddress = [companyAddress, companyCity, companyState, companyPincode].filter(Boolean).join(', ');

  // Common header component
  const Header = () => (
    <div className="flex items-start justify-between mb-4 pb-2 border-b-2 border-yellow-500">
      <div className="flex-shrink-0 flex items-center" style={{ width: '180px' }}>
        {companyLogo ? (
          <img
            src={companyLogo}
            alt={companyName}
            className="h-20 w-auto object-contain"
            onError={(e) => {
              console.error('Logo failed to load:', companyLogo);
              const fileId = companyLogo.match(/id=([^&]+)/)?.[1];
              if (fileId && !e.target.dataset.retried) {
                e.target.dataset.retried = 'true';
                e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        ) : (
          <div className="h-20 w-24 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">
            LOGO
          </div>
        )}
      </div>
      <div className="text-right" style={{ fontSize: '10px' }}>
        <p className="font-semibold">Freight Forwarder</p>
        <p className="font-semibold">Custom Broker</p>
        <p className="font-semibold">Reefer Transportation</p>
        <p className="font-semibold">Logistics Solution</p>
        {companyWebsite && <p className="text-blue-600 mt-1">{companyWebsite}</p>}
      </div>
    </div>
  );

  // Common footer component
  const Footer = () => (
    <div className="mt-auto pt-3 border-t-2 border-yellow-500">
      <div className="text-center" style={{ fontSize: '9px' }}>
        <p className="font-bold">{companyName}</p>
        {fullAddress && <p className="mt-1"><strong>Reg Off:</strong> {fullAddress}</p>}
        <p className="mt-1">
          {companyGst && <span><strong>GST No.:</strong> {companyGst}</span>}
          {companyEmail && <span> <strong>Email:</strong> {companyEmail}</span>}
          {companyLlp && <span> <strong>LLP IN No.:</strong> {companyLlp}</span>}
        </p>
        {companyPhone && <p><strong>Tel:</strong> {companyPhone}</p>}
      </div>
    </div>
  );

  return (
    <>
      <div id="lifting-advice-print-pack" className="bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
        
        {/* PAGE 1: LIFTING ADVICE */}
        <div className="print-page page-1" style={{ pageBreakAfter: 'always' }}>
          <div className="p-8 flex flex-col h-full">
            <Header />
            
            <div className="text-right mb-4" style={{ fontSize: '11px' }}>
              <p><strong>Date :</strong> {formatDate(advice?.date)}</p>
            </div>

            <h1 className="text-center font-bold text-2xl mb-6">{advice?.airways || 'AIR ARABIA AIRWAYS'}</h1>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div style={{ fontSize: '11px' }}>
                <p className="font-semibold">TO,</p>
                {advice?.to_location_text ? (
                  <>
                    <p className="font-semibold whitespace-pre-line">{advice.to_location_text}</p>
                    {custodianAddress && (
                      <p className="font-normal whitespace-pre-line mt-1">{custodianAddress}</p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-semibold">THE MANAGER,</p>
                    <p className="font-semibold">CSC/GSEC</p>
                    <p className="font-semibold">AIR CARGO COMPLEX,</p>
                    <p className="font-semibold">AHMEDABAD</p>
                  </>
                )}
              </div>
              <div className="text-right" style={{ fontSize: '11px' }}>
                <p><strong>LEO No :</strong> {advice?.leo_no || ''}</p>
                <p><strong>Date :</strong> {formatDate(advice?.leo_date)}</p>
              </div>
            </div>

            <h2 className="text-center font-bold text-xl mb-4">LIFTING ADVICE</h2>

            <table className="w-full border-collapse border-2 border-black mb-8" style={{ fontSize: '11px' }}>
              <tbody>
                <tr>
                  <td className="border border-black p-2 font-semibold w-1/2">MAWB NO.</td>
                  <td className="border border-black p-2">{advice?.mawb_no || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold">HAWB NO.</td>
                  <td className="border border-black p-2">{advice?.hawb_no || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold">S/B/ NO. & DATE</td>
                  <td className="border border-black p-2">{advice?.sb_no || ''} {advice?.sb_date ? formatDate(advice.sb_date) : ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold">G.S.E.C CHALLAN NO.</td>
                  <td className="border border-black p-2">{advice?.gsec_challan_no || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold">NO. OF PCS.</td>
                  <td className="border border-black p-2">{advice?.no_of_pieces || ''}</td>
                </tr>
                <tr>
                  <td className="border border-black p-2 font-semibold">GR. WEIGHT</td>
                  <td className="border border-black p-2">{advice?.gross_weight || ''}</td>
                </tr>
              </tbody>
            </table>

            <div className="mb-20" style={{ fontSize: '11px' }}>
              <p>For, {companyName}</p>
            </div>

            <p style={{ fontSize: '11px' }}>Authorized Signatory</p>

            <Footer />
          </div>
        </div>

        {/* PAGE 2: ANNEXURE - 1 */}
        <div className="print-page page-2" style={{ pageBreakAfter: 'always' }}>
          <div className="p-8 flex flex-col h-full">
            <Header />
            
            <div className="text-right mb-4" style={{ fontSize: '11px' }}>
              <p><strong>Date :</strong> {formatDate(advice?.date)}</p>
            </div>

            <h2 className="text-center font-bold text-xl mb-4">Appendix / Annexure - 1</h2>
            <h2 className="text-center font-bold text-xl mb-6">CARGO SECURITY DECLARATION</h2>

            <p className="text-center mb-6" style={{ fontSize: '11px', lineHeight: '1.6' }}>
              The cargo as per details given below dispatched by (IATA) Comes from customers whose reliability we do not doubt. The cargo has been protected from the time it was tendered to and accepted by us at our acceptance location.
            </p>

            <p className="mb-4" style={{ fontSize: '11px' }}><strong>Master Air Way Bill No. : {advice?.mawb_no || ''}</strong></p>

            <table className="w-full border-collapse border border-black mb-6" style={{ fontSize: '10px' }}>
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black p-2">S.N.</th>
                  <th className="border border-black p-2">HAWB NO.</th>
                  <th className="border border-black p-2">CONTENTS</th>
                  <th className="border border-black p-2">PCS.</th>
                  <th className="border border-black p-2">WEIGHT</th>
                  <th className="border border-black p-2">FROM</th>
                  <th className="border border-black p-2">TO</th>
                </tr>
              </thead>
              <tbody>
                {hawbRows.map((row) => (
                  <tr key={row.sn}>
                    <td className="border border-black p-2 text-center">{row.sn}</td>
                    <td className="border border-black p-2 font-medium">{row.hawb_no}</td>
                    <td className="border border-black p-2">{row.contents}</td>
                    <td className="border border-black p-2 text-center">{row.pieces}</td>
                    <td className="border border-black p-2 text-center">{row.weight}</td>
                    <td className="border border-black p-2">{row.from}</td>
                    <td className="border border-black p-2">{row.to}</td>
                  </tr>
                ))}
                {hawbRows.length > 1 && (
                  <tr className="font-bold bg-gray-50">
                    <td className="border border-black p-2 text-center" colSpan={3}>TOTAL</td>
                    <td className="border border-black p-2 text-center">{advice?.no_of_pieces || ''}</td>
                    <td className="border border-black p-2 text-center">{advice?.gross_weight ? Number(advice.gross_weight).toFixed(2) : ''}</td>
                    <td className="border border-black p-2" colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="mb-4" style={{ fontSize: '11px' }}>
              <p><strong>Name :</strong> {advice?.employee_name || employeeName || advice?.employee_id || 'EXPORT DOCUMENTATION'}</p>
              <p><strong>Designation :</strong> EXPORT DOCUMENTATION</p>
              <p><strong>Date :</strong> {formatDate(advice?.date)}</p>
            </div>

            <div className="mb-20" style={{ fontSize: '11px' }}>
              <p>For, {companyName}</p>
            </div>

            <p style={{ fontSize: '11px' }}>Authorized Signatory</p>

            <Footer />
          </div>
        </div>

        {/* PAGE 3: GENERAL CARGO SECURITY DECLARATION */}
        <div className="print-page page-3" style={{ pageBreakAfter: 'always' }}>
          <div className="p-8 flex flex-col h-full">
            <Header />
            
            <div className="text-right mb-4" style={{ fontSize: '11px' }}>
              <p><strong>Date :</strong> {formatDate(advice?.date)}</p>
            </div>

            <h2 className="text-center font-bold text-xl mb-6">GENERAL CARGO SECURITY DECLARATION</h2>

            <p className="text-center mb-8" style={{ fontSize: '11px', lineHeight: '1.8' }}>
              The personal effects house hold goods that are accounted for on AWB and cargo from unknown customers and / or customers whose reliability is doubtful are delivered separately and identified to the air carrier for security our customers have been informed that cargo can be subject to measures for the purpose of ensuring the security of air traffic.
            </p>

            <div className="mb-8" style={{ fontSize: '11px' }}>
              <p className="mb-4">Thanking You with Kind Regards,</p>
              <p>For, {companyName}</p>
            </div>

            <div className="mb-20">
              <p style={{ fontSize: '11px' }}>Authorized Signatory</p>
            </div>

            <Footer />
          </div>
        </div>

        {/* PAGE 4: ANNEXTURE - II */}
        <div className="print-page page-4" style={{ pageBreakAfter: 'always' }}>
          <div className="p-8 flex flex-col h-full">
            <Header />
            
            <div className="text-right mb-4" style={{ fontSize: '11px' }}>
              <p><strong>APPENDIX / ANNEXTURE - II</strong></p>
            </div>

            <h2 className="text-center font-bold text-xl mb-6">CARGO SECURITY DECLARATION</h2>

            <p className="text-center mb-6" style={{ fontSize: '11px', lineHeight: '1.6' }}>
              The personal effects/ house hold goods/ cargo as per details given below dispatched by (IATA Name) comes from unknown customers and/ or customers whose reliability is doubtful. The cargo has been protected from the time it was tendered to and accepted by us at our acceptance location and the same has been identified to the aircraft operator for security measure. Our customers have been informed that cargo can be subjected to prescribed security measures for the purpose of ensuring the security of air traffic
            </p>

            <p className="mb-4" style={{ fontSize: '11px' }}><strong>Master Air Way Bill No. : {advice?.mawb_no || ''}</strong></p>

            <table className="w-full border-collapse border border-black mb-6" style={{ fontSize: '10px' }}>
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-black p-2">S.N.</th>
                  <th className="border border-black p-2">HAWB NO.</th>
                  <th className="border border-black p-2">CONTENTS</th>
                  <th className="border border-black p-2">PCS.</th>
                  <th className="border border-black p-2">WEIGHT</th>
                  <th className="border border-black p-2">FROM</th>
                  <th className="border border-black p-2">TO</th>
                </tr>
              </thead>
              <tbody>
                {hawbRows.map((row) => (
                  <tr key={row.sn}>
                    <td className="border border-black p-2 text-center">{row.sn}</td>
                    <td className="border border-black p-2 font-medium">{row.hawb_no}</td>
                    <td className="border border-black p-2">{row.contents}</td>
                    <td className="border border-black p-2 text-center">{row.pieces}</td>
                    <td className="border border-black p-2 text-center">{row.weight}</td>
                    <td className="border border-black p-2">{row.from}</td>
                    <td className="border border-black p-2">{row.to}</td>
                  </tr>
                ))}
                {hawbRows.length > 1 && (
                  <tr className="font-bold bg-gray-50">
                    <td className="border border-black p-2 text-center" colSpan={3}>TOTAL</td>
                    <td className="border border-black p-2 text-center">{advice?.no_of_pieces || ''}</td>
                    <td className="border border-black p-2 text-center">{advice?.gross_weight ? Number(advice.gross_weight).toFixed(2) : ''}</td>
                    <td className="border border-black p-2" colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="mb-4" style={{ fontSize: '11px' }}>
              <p><strong>Name :</strong> {advice?.employee_name || employeeName || advice?.employee_id || 'EXPORT DOCUMENTATION'}</p>
              <p><strong>Designation :</strong> EXPORT DOCUMENTATION</p>
              <p><strong>Date :</strong> {formatDate(advice?.date)}</p>
            </div>

            <div className="mb-20" style={{ fontSize: '11px' }}>
              <p>For, {companyName}</p>
            </div>

            <p style={{ fontSize: '11px' }}>Authorized Signatory</p>

            <Footer />
          </div>
        </div>

        {/* PAGE 5: MAWB STATEMENT */}
        <div className="print-page page-5">
          <div className="p-8 flex flex-col h-full">
            <Header />

            <h2 className="font-bold text-lg mb-4">Master Air waybill (MAWB) Statement</h2>

            <p style={{ fontSize: '10px', lineHeight: '1.6' }} className="mb-4">
              All shipments tendered in this MAWB were received directly from a shipper, or other person with an established relationship with (IATA NAME): {companyName} (insert name of regulated agent or freight forwarder) for at least 180 calendar days, which has an established shipping address, and a payment, credit or invoice history of at least 180 calendar days OR a person originating or tendering a shipment where (IATA NAME) {companyName} (insert name of regulated agent or freight forwarder) has an established business relationship* or payment, credit, or invoice history with the consignee or bill-to party of at least 180 calendar days. All shipments tendered in this MAWB were accepted via a secure transport, either directly from the shipper, or picked up by (IATA NAME) {companyName} (insert name of regulated agent or freight forwarder) or agent.
            </p>

            <p style={{ fontSize: '9px', lineHeight: '1.5' }} className="mb-6 italic">
              *An established business relationship is an association between persons ("persons" includes individuals and entities) that may also include a documented history of sales contacts or activities, other documented correspondence, or business records.
            </p>

            <h3 className="font-bold text-center text-lg mb-4">Statement of Declaration</h3>

            <p style={{ fontSize: '10px', lineHeight: '1.6' }} className="mb-4">
              I confirm this consignment does not contain cargo that has either originated, transferred from, or transited through any points in Egypt, Yemen, Syria and Somalia.
            </p>

            <div style={{ fontSize: '10px' }} className="mb-4">
              <p><strong>Air waybill Number :</strong> {advice?.mawb_no || ''}</p>
              <p><strong>ORIGIN :</strong> {advice?.from_location_name || ''} <strong>Destination :</strong> {advice?.to_location_final_name || ''}</p>
              <p><strong>Name of the company agent, forwarder, consolidator or air carrier tendering shipment: (IATA NAME) :</strong> {companyName}</p>
              <p><strong>Date:</strong> {formatDate(advice?.date)} <strong>Name:</strong> {advice?.employee_name || employeeName || advice?.employee_id || ''} <strong>Signature:</strong>____________________</p>
            </div>

            <p style={{ fontSize: '10px' }} className="mb-4">
              This statement must be retained by accepting carrier at origin for 90 calendar days.
            </p>

            <p style={{ fontSize: '10px' }}>
              <strong>DT:</strong> {formatDate(advice?.date)}
            </p>

            <Footer />
          </div>
        </div>

      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
          }

          body * {
            visibility: hidden;
          }

          #lifting-advice-print-pack,
          #lifting-advice-print-pack * {
            visibility: visible;
          }

          #lifting-advice-print-pack {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-page {
            width: 210mm;
            height: 297mm;
            page-break-after: always;
            page-break-inside: avoid;
            box-sizing: border-box;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .print-page:last-child {
            page-break-after: auto;
          }

          .print-page > div {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
        }

        @media screen {
          #lifting-advice-print-pack {
            max-width: 210mm;
            margin: 0 auto;
          }

          .print-page {
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            margin-bottom: 20px;
            background: white;
            min-height: 297mm;
            display: flex;
            flex-direction: column;
          }

          .print-page > div {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
        }
      `}</style>
    </>
  );
});

LiftingAdvicePrint.displayName = 'LiftingAdvicePrint';

export default LiftingAdvicePrint;
